import { useEffect, useRef, useState } from 'react';
import type Hls from 'hls.js';
import { iptv } from '../api';
import { linkProps, navigate } from '../router';
import { hhmm, dayText, matchHref } from '../sportcenter';
import { hubApi, statusText, teamColor, type HubCard, type HubChannel, type HubSide } from '../sporthub';
import { ChannelLogo, playLink } from './LiveBits';
import { Crest } from './SportCenterBits';

/**
 * Bouwstenen van de Sports Hub (web): de grote wedstrijd, kaarten in clubkleuren, "Waar kijk je?"
 * met herstellen en herinneren, en een klein venster om alvast mee te kijken.
 */

const when = (c: HubCard) => (dayText(c.start) === 'Vandaag' ? hhmm(c.start) : `${dayText(c.start)} ${hhmm(c.start)}`);

function Badge({ side, size = 44 }: { side: HubSide; size?: number }) {
  return <Crest logo={side.logo} name={side.short} size={size} />;
}

/** Score, of "verborgen" als je uitslagen verbergt. */
function Score({ c, hide, big = false }: { c: HubCard; hide: boolean; big?: boolean }) {
  if (c.state === 'pre' || !c.home || !c.away) return <span className={big ? 'sh-score-big is-pre' : 'sh-score is-pre'}>{c.state === 'pre' ? hhmm(c.start) : ''}</span>;
  const text = `${c.home.score ?? '-'} – ${c.away.score ?? '-'}`;
  return <span className={`${big ? 'sh-score-big' : 'sh-score'}${hide ? ' is-hidden' : ''}`} data-score aria-label={hide ? 'Uitslag verborgen' : text}>{text}</span>;
}

/** De belangrijkste wedstrijd groot, in de kleuren van beide clubs. */
export function HeroCard({ c, hide, onOpen, onWindow }: { c: HubCard; hide: boolean; onOpen: (c: HubCard) => void; onWindow: (c: HubCard) => void }) {
  const best = c.channels[0];
  const bg = c.home && c.away ? `linear-gradient(100deg, ${teamColor(c.home, '#7d0b1f')} 0%, color-mix(in srgb, ${teamColor(c.home, '#7d0b1f')} 55%, #000) 47%, color-mix(in srgb, ${teamColor(c.away, '#0b2a86')} 55%, #000) 53%, ${teamColor(c.away, '#0b2a86')} 100%)` : 'linear-gradient(110deg, #3a0d0d, #8c1a14)';
  const goals = (s: 'home' | 'away') => c.scorers.filter((x) => x.side === s).map((x) => `${x.player.split(' ').slice(-1)[0]} ${x.minute}`).join(' · ');
  return (
    <section className="sh-hero" style={{ background: bg }} aria-label={c.title}>
      <div className="sh-hero-top">
        {c.state === 'in' ? <span className="sh-live">● LIVE {statusText(c)}</span> : <span className="sh-when">{when(c)}</span>}
        {c.fav && <span className="sh-tag">★ JIJ VOLGT</span>}
        <span className="sh-hero-league">{c.leagueName}{c.venue ? ` · ${c.venue}` : ''}</span>
      </div>
      {c.home && c.away ? (
        <div className="sh-hero-teams">
          <span className="sh-hero-team"><Badge side={c.home} size={88} /><b>{c.home.short}</b></span>
          <Score c={c} hide={hide} big />
          <span className="sh-hero-team is-away"><b>{c.away.short}</b><Badge side={c.away} size={88} /></span>
        </div>
      ) : (
        <div className="sh-hero-event"><b>{c.title}</b>{c.sub && <span>{c.sub}</span>}</div>
      )}
      {!hide && c.scorers.length > 0 && <div className="sh-hero-goals"><span>{goals('home')}</span><span>{goals('away')}</span></div>}
      <div className="sh-hero-actions" data-focus-group>
        {best ? (
          <a className="btn btn-primary" {...linkProps(playLink(best.accountId, best.streamId))} data-autofocus>▶ {c.state === 'in' ? 'Kijk live' : 'Kijk'} · {best.name}</a>
        ) : (
          <span className="sh-nochannel">Niet gevonden op jouw zenders</span>
        )}
        <button className="btn" onClick={() => onOpen(c)}>{c.channels.length > 1 ? `${c.channels.length} zenders` : 'Zenders'}</button>
        {best && <button className="btn" onClick={() => onWindow(c)}>Kijk in het venster</button>}
        {c.path && c.eventId && <a className="btn" {...linkProps(matchHref({ path: c.path, eventId: c.eventId }))}>Wedstrijdcentrum</a>}
      </div>
    </section>
  );
}

/** Kaart in de rijen: competitie, status, beide clubs met kleur en stand, tijd en zender. */
export function HubCardView({ c, hide, onOpen }: { c: HubCard; hide: boolean; onOpen: (c: HubCard) => void }) {
  const best = c.channels[0];
  const tag = c.state === 'in' ? <span className="sh-pill is-live">● {statusText(c)}</span> : c.state === 'post' ? <span className="sh-pill">Afgelopen</span> : <span className="sh-pill is-later">{dayText(c.start) === 'Vandaag' ? hhmm(c.start) : dayText(c.start)}</span>;
  return (
    <button type="button" className={`sh-card${c.fav ? ' is-fav' : ''}`} onClick={() => onOpen(c)} aria-label={`${c.title}, ${c.leagueName}${best ? `, op ${best.name}` : ''}`}>
      <span className="sh-card-head"><span>{c.leagueName}</span>{tag}</span>
      {c.home && c.away ? (
        <>
          <span className="sh-card-team" style={{ background: `linear-gradient(90deg, color-mix(in srgb, ${teamColor(c.home)} 55%, transparent), transparent)` }}>
            <Badge side={c.home} size={30} /><b>{c.home.short}</b>{c.state !== 'pre' && <i data-score className={hide ? 'is-hidden' : ''}>{c.home.score}</i>}
          </span>
          <span className="sh-card-team" style={{ background: `linear-gradient(90deg, color-mix(in srgb, ${teamColor(c.away)} 55%, transparent), transparent)` }}>
            <Badge side={c.away} size={30} /><b>{c.away.short}</b>{c.state !== 'pre' && <i data-score className={hide ? 'is-hidden' : ''}>{c.away.score}</i>}
          </span>
        </>
      ) : (
        <span className="sh-card-event"><b>{c.title}</b>{c.sub && <span>{c.sub}</span>}</span>
      )}
      <span className="sh-card-foot">
        <span>{c.state === 'pre' ? when(c) : c.venue ?? c.detail}</span>
        {best ? <span className="sh-ch">{best.name}</span> : <span className="sh-ch is-none">geen zender</span>}
      </span>
    </button>
  );
}

/** "Waar kijk je?": alle zenders, beste eerst; kijken, venster, herstellen, herinneren. */
export function ChannelSheet({ c, onClose, onWindow }: { c: HubCard; onClose: () => void; onWindow: (c: HubCard, ch: HubChannel) => void }) {
  const [list, setList] = useState<HubChannel[]>(c.channels);
  const [fixing, setFixing] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<{ accountId: string; streamId: number; name: string; logo: string | null }[]>([]);
  const [remind, setRemind] = useState(c.remind);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    hubApi.channels(c).then((r) => setList(r.channels)).catch(() => undefined);
  }, [c]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace') {
        if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  useEffect(() => {
    if (!fixing) return;
    const t = window.setTimeout(() => {
      iptv.channels().then((r) => {
        const words = (q || c.title).toLowerCase().split(/\s+/).filter((w) => w.length > 1);
        setFound(r.channels.filter((x) => words.some((w) => x.name.toLowerCase().includes(w))).slice(0, 30).map((x) => ({ accountId: x.accountId, streamId: x.streamId, name: x.name, logo: x.logo })));
      }).catch(() => setFound([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [fixing, q, c.title]);

  const pickFix = async (ch: { accountId: string; streamId: number; name: string }) => {
    await hubApi.fix(c.key, ch).catch(() => undefined);
    const r = await hubApi.channels(c).catch(() => null);
    if (r) setList(r.channels);
    setFixing(false);
    setMsg(`Onthouden: ${ch.name} voor deze wedstrijd.`);
  };
  const toggleRemind = async (auto: boolean) => {
    const best = list[0];
    const off = remind && !auto;
    await hubApi.remind({ key: c.key, title: c.title, start: c.start, auto, channel: best ? { accountId: best.accountId, streamId: best.streamId, name: best.name } : null, off }).catch(() => undefined);
    setRemind(!off);
    setMsg(off ? 'Herinnering uit.' : auto ? 'Je krijgt 15 minuten van tevoren een melding en tumedia schakelt 1 minuut voor de aftrap over.' : 'Je krijgt 15 minuten van tevoren een melding.');
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal sh-sheet" role="dialog" aria-modal="true" aria-label={`Waar kijk je ${c.title}?`} onClick={(e) => e.stopPropagation()}>
        <div className="sh-sheet-head">
          <h2>Waar kijk je?</h2>
          <span className="subtle">{c.title} · {c.leagueName} · {c.state === 'in' ? statusText(c) : when(c)}</span>
        </div>
        {!fixing && (
          <>
            {list.length === 0 && <p className="subtle">Niet gevonden in de gids van je zenders. Kies hieronder zelf de zender.</p>}
            <div className="sh-ch-list" data-focus-group>
              {list.map((ch, i) => (
                <a key={`${ch.accountId}:${ch.streamId}`} className={`sh-ch-row${i === 0 ? ' is-best' : ''}`} {...linkProps(playLink(ch.accountId, ch.streamId))} data-autofocus={i === 0 ? '' : undefined}>
                  <ChannelLogo src={ch.logo} name={ch.name} size={48} />
                  <span className="sh-ch-text">
                    <b>{ch.name}{ch.quality && <i className="sh-q">{ch.quality}</i>}</b>
                    <span className="subtle">{ch.provider} · {ch.reason}</span>
                  </span>
                  {ch.fixed ? <span className="sh-best">Door jou gekozen</span> : i === 0 ? <span className="sh-best">Beste match</span> : null}
                </a>
              ))}
            </div>
            <div className="form-actions sh-sheet-actions">
              {list[0] && <button className="btn" onClick={() => { onWindow(c, list[0]); onClose(); }}>Kijk in het venster</button>}
              {c.path && c.eventId && <a className="btn" {...linkProps(matchHref({ path: c.path, eventId: c.eventId }))}>Wedstrijdcentrum</a>}
              {c.state === 'pre' && <button className="btn" onClick={() => void toggleRemind(false)}>{remind ? 'Herinnering uit' : 'Herinner mij'}</button>}
              {c.state === 'pre' && <button className="btn" onClick={() => void toggleRemind(true)}>Schakel 1 min voor de aftrap over</button>}
              <button className="btn btn-ghost" onClick={() => setFixing(true)}>Verkeerde zender? Herstel</button>
            </div>
          </>
        )}
        {fixing && (
          <div className="sh-fix">
            <label className="ed-field">Zoek de juiste zender
              <input className="text-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="bijv. Ziggo Sport 2" autoFocus />
            </label>
            <div className="sh-ch-list" data-focus-group>
              {found.map((ch) => (
                <button key={`${ch.accountId}:${ch.streamId}`} className="sh-ch-row" onClick={() => void pickFix(ch)}>
                  <ChannelLogo src={ch.logo} name={ch.name} size={40} />
                  <span className="sh-ch-text"><b>{ch.name}</b></span>
                </button>
              ))}
              {!found.length && <p className="subtle">Geen zenders gevonden met deze naam.</p>}
            </div>
            <div className="form-actions">
              <button className="btn" onClick={() => setFixing(false)}>Terug</button>
              {list.some((x) => x.fixed) && <button className="btn btn-ghost" onClick={() => void hubApi.fix(c.key, null).then(() => hubApi.channels(c)).then((r) => { setList(r.channels); setFixing(false); }).catch(() => undefined)}>Mijn keuze weghalen</button>}
            </div>
          </div>
        )}
        {msg && <p className="subtle" role="status">{msg}</p>}
        <button className="btn btn-ghost sh-close" onClick={onClose} aria-label="Sluiten">✕</button>
      </div>
    </div>
  );
}

/** Klein venster: kijk alvast mee terwijl je verder bladert (gebruikt één IPTV-verbinding). */
export function PreviewWindow({ ch, title, onClose }: { ch: HubChannel; title: string; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let sid: string | null = null;
    let hls: Hls | null = null;
    let cancelled = false;
    setError(null);
    iptv.play(ch.accountId, ch.streamId, { slot: 'preview', fmp4: true })
      .then(async (r) => {
        sid = r.sessionId;
        if (cancelled) { void iptv.stop(r.sessionId); return; }
        if (el.canPlayType('application/vnd.apple.mpegurl')) { el.src = r.url; void el.play().catch(() => undefined); return; }
        const { default: HlsLib } = await import('hls.js');
        if (cancelled) return;
        hls = new HlsLib({ liveSyncDurationCount: 3, maxBufferLength: 20 });
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => void el.play().catch(() => undefined));
        hls.loadSource(r.url);
        hls.attachMedia(el);
      })
      .catch(() => setError('Deze zender start nu niet.'));
    return () => {
      cancelled = true;
      hls?.destroy();
      el.removeAttribute('src');
      el.load();
      if (sid) void iptv.stop(sid);
    };
  }, [ch.accountId, ch.streamId]);
  return (
    <aside className="sh-window" aria-label={`Venster: ${title}`}>
      <video ref={video} muted={muted} playsInline className="sh-window-video" />
      {error && <span className="sh-window-error">{error}</span>}
      <div className="sh-window-bar">
        <span>{ch.name} · {title}</span>
        <span className="sh-window-btns">
          <button className="icon-mini" onClick={() => setMuted((m) => !m)} aria-label={muted ? 'Geluid aan' : 'Geluid uit'}>{muted ? '🔇' : '🔊'}</button>
          <button className="icon-mini" onClick={() => navigate(playLink(ch.accountId, ch.streamId))} aria-label="Volledig scherm">⤢</button>
          <button className="icon-mini" onClick={onClose} aria-label="Venster sluiten">✕</button>
        </span>
      </div>
    </aside>
  );
}
